import React, { useEffect, useState } from "react";
import { List, Button, Typography, Modal, Flex } from "antd";
import { toast } from "react-toastify";
import { CloseOutlined } from "@ant-design/icons";
import CustomLoader from "../../../common/UI/Loader";
import moment from "moment";
import apiInstance from "../../../../services/apiService";
import ENDPOINTS from "../../../../services/endpoint";
import "./index.scss";

interface SubscribedAddonListProps {
  setSubscribedAddonBillingPrice: any;
  showOnlyPhoneNumbers?: boolean;
  phoneNumberToDelete?: string;
  loading: boolean;
  setLoading: any;
  getAddonsData: any;
  addonData: any;
  refreshCancelledNumbers: any;
}

interface Country {
  countryCode: number;
  countryName: string;
  isoCode: string;
  iso3Code: string;
}

// interface AllAddons {
//   _id: string;
//   addonType: number;
//   addonName: string;
//   addonCost: string;
//   countries: Country[];
// }

interface BillingDetails {
  status: string;
  current_term_end: string;
  next_billing_at: string;
  billing_period_unit: string;
  currency_code: string;
  mrr: number;
  subId: string;
  scheduleChanges: boolean;
  scheduledAddons: any[];
  scheduleMessage: string;
}

interface CallAddon {
  _id: string;
  addonId: string;
  addonType: number;
  addonName: string;
  cost: string;
  totalCost: number;
  quantity: number;
  countries: Country[];
}

interface SMSAddon {
  _id: string;
  addonId: string;
  addonType: number;
  addonName: string;
  cost: string;
  totalCost: number;
  quantity: number;
  countries: Country[];
  phoneNumber: string;
  phoneId: string;
}

interface PhoneAddon {
  phoneId: string;
  phoneNumber: string;
  addonName: string;
  cost: string;
}

const { Title, Text } = Typography;

const SubscribedAddonList: React.FC<SubscribedAddonListProps> = ({
  setSubscribedAddonBillingPrice,
  showOnlyPhoneNumbers = false,
  phoneNumberToDelete = "",
  loading,
  setLoading,
  getAddonsData,
  addonData,
  refreshCancelledNumbers,
}) => {
  // const [loading, setLoading] = useState<boolean>(false);
  const [unsubscribeInProcess, setUnsubscribeInProcess] =
    useState<boolean>(false);
  // const [addonData, setAddonData] = useState<any>();
  // const [allAddons, setAllAddons] = useState<AllAddons[]>([]);
  const [callAddons, setCallAddons] = useState<CallAddon[]>([]);
  const [smsAddons, setSMSAddons] = useState<SMSAddon[]>([]);
  const [phoneAddons, setPhoneAddons] = useState<PhoneAddon[]>([]);
  const [isConfirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [confirmationModalData, setConfirmationModalData] = useState<any>(null);
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(
    null
  );
  const [cancelNowLoading, setCancelNowLoading] = useState<boolean>(false);
  const [cancelLaterLoading, setCancelLaterLoading] = useState<boolean>(false);
  // const [highlightedPhoneNumber, setHighlightedPhoneNumber] = useState();

  // const getAddonsData = async () => {
  //   const url = ENDPOINTS.SUBSCRIBED_ADDON_DETAILS;

  //   setLoading(true);
  //   try {
  //     const response = await apiInstance.get(url);
  //     const data = response.data.data;
  //     setAddonData(data);
  //   } catch (error: any) {
  //     customLogger(error);
  //     toast.error(error.message || "There was an error. Please try again");
  //     setLoading(false);
  //   }
  // };

  const handleDelete = async (item: any, cancelImmediately: string) => {
    let url = "";
    let data: any = null;

    switch (item.addonType) {
      case 1:
        url = ENDPOINTS.CANCEL_CALL;
        data = {
          addonIds: [item._id],
          cancelImmediately,
        };
        break;

      case 2:
        url = ENDPOINTS.CANCEL_SMS;
        data = {
          phoneId: item.phoneId,
          addonId: item.addonId,
          cancelImmediately,
        };
        break;

      case undefined:
        url = ENDPOINTS.CANCEL_PHONE + "/" + (item.phoneId || item._id);
        data = {
          cancelImmediately,
        };
        break;

      default:
        toast.error("Invalid add-on type");
        return;
    }

    setUnsubscribeInProcess(true);
    try {
      const response = await apiInstance.delete(
        url,
        data ? { data } : undefined
      );
      const message = response.data.message;
      toast.success(message || "Unsubscribed successfully");
      setConfirmationModalOpen(false);
      getAddonsData();
      refreshCancelledNumbers();
    } catch (error: any) {
      toast.error(error.message || "Failed to unsubscribe");
    } finally {
      setUnsubscribeInProcess(false);
      setCancelLaterLoading(false);
      setCancelNowLoading(false);
    }
  };

  useEffect(() => {
    getAddonsData();
  }, [getAddonsData]);

  useEffect(() => {
    if (addonData) {
      if (showOnlyPhoneNumbers) {
        setPhoneAddons(addonData.phoneAddons);
      } else {
        // setAllAddons(addonData.getAllAddons);
        setCallAddons(addonData.callsAddons);
        setSMSAddons(addonData.smsAddons);
        setPhoneAddons(addonData.phoneAddons);
      }
      setBillingDetails(addonData.billingDetails);
      setSubscribedAddonBillingPrice(() => {
        if (addonData?.billingDetails) {
          return `${addonData.billingDetails.currency_code} ${addonData.billingDetails.mrr}`;
        }
        return "";
      });
      setLoading(false);
    }
  }, [
    addonData,
    setLoading,
    setSubscribedAddonBillingPrice,
    showOnlyPhoneNumbers,
  ]);

  useEffect(() => {
    if (phoneNumberToDelete) {
      setPhoneAddons((prevAddons) => {
        const addonsCopy = [...prevAddons];
        const foundIndex = addonsCopy.findIndex(
          (addon) => addon.phoneId === phoneNumberToDelete
        );

        if (foundIndex === -1) {
          return addonsCopy;
        }

        const [item] = addonsCopy.splice(foundIndex, 1);
        addonsCopy.unshift(item);
        return addonsCopy;
      });
    }
  }, [phoneAddons, phoneNumberToDelete]);

  return (
    <div className="addon-list-container">
      {loading ? (
        <Flex className="w-full h-full" justify="center" align="center">
          <CustomLoader />
        </Flex>
      ) : addonData?.callsAddons.length > 0 ||
        addonData?.smsAddons.length > 0 ||
        addonData?.phoneAddons.length > 0 ? (
        <>
          {/* {allAddons.length && (
            <List
              key="all-addons"
              className="addon-list"
              dataSource={allAddons}
              renderItem={(item: any) => (
                <List.Item className="addon-item">
                  <div>
                    <Title level={5} className="addon-title">
                      {item.addonName}{" "}
                      <span
                        style={{
                          color: item.status === "active" ? "green" : "red",
                          textTransform: "capitalize",
                          fontSize: "14px",
                        }}
                      >
                        {item.status}
                      </span>
                    </Title>
                    <Text className="addon-description">
                      {item.addonCost}$ Monthly
                    </Text>
                    <br />
                    <Text className="addon-description">
                      Next billing date:{" "}
                      {moment(billingDetails?.next_billing_at).format(
                        "DD MMMM YYYY"
                      )}
                    </Text>
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmationModalOpen(true);
                      setConfirmationModalData(item);
                    }}
                    type="text"
                    className="addon-button"
                    disabled={unsubscribeInProcess}
                  >
                    Unsubscribe
                  </Button>
                </List.Item>
              )}
            />
          )} */}

          {callAddons?.length > 0 && (
            <List
              key="call-addons"
              className="addon-list"
              dataSource={callAddons}
              renderItem={(item: any) => (
                <List.Item className="addon-item">
                  <div>
                    <Title level={5} className="addon-title">
                      {item.addonName}{" "}
                      <span
                        style={{
                          color: item.status === "active" ? "green" : "red",
                          textTransform: "capitalize",
                          fontSize: "14px",
                        }}
                      >
                        {item.status}
                      </span>
                    </Title>
                    <Text className="addon-description">
                      {item.cost}$ Monthly
                    </Text>
                    <br />
                    <Text className="addon-description">
                      Next billing date:{" "}
                      {moment(billingDetails?.next_billing_at).format(
                        "DD MMMM YYYY"
                      )}
                    </Text>
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmationModalOpen(true);
                      setConfirmationModalData(item);
                    }}
                    type="text"
                    className="addon-button"
                    disabled={unsubscribeInProcess}
                  >
                    Unsubscribe
                  </Button>
                </List.Item>
              )}
            />
          )}

          {smsAddons?.length > 0 && (
            <List
              key="sms-addons"
              className="addon-list"
              dataSource={smsAddons}
              renderItem={(item: any) => (
                <List.Item className="addon-item">
                  <div>
                    <Title level={5} className="addon-title">
                      {item.addonName}{" "}
                      <span
                        style={{
                          color: item.status === "active" ? "green" : "red",
                          textTransform: "capitalize",
                          fontSize: "14px",
                        }}
                      >
                        {item.status}
                      </span>
                    </Title>
                    <Text className="addon-description">
                      {item.phoneNumber}
                    </Text>
                    <br />
                    <Text className="addon-description">
                      {item.cost}$ Monthly
                    </Text>
                    <br />
                    <Text className="addon-description">
                      Next billing date:{" "}
                      {moment(billingDetails?.next_billing_at).format(
                        "DD MMMM YYYY"
                      )}
                    </Text>
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmationModalOpen(true);
                      setConfirmationModalData(item);
                    }}
                    type="text"
                    className="addon-button"
                    disabled={unsubscribeInProcess}
                  >
                    Unsubscribe
                  </Button>
                </List.Item>
              )}
            />
          )}

          {phoneAddons?.length > 0 && (
            <List
              key="phone-addons"
              className="addon-list"
              dataSource={phoneAddons}
              renderItem={(item: any) => (
                <List.Item
                  key={item.phoneId}
                  className={`addon-item ${
                    item.phoneId === phoneNumberToDelete
                      ? "highlighted-item"
                      : ""
                  }`}
                >
                  <div>
                    <Title level={5} className="addon-title">
                      {item.addonName}{" "}
                      <span
                        style={{
                          color: item.status === "active" ? "green" : "red",
                          textTransform: "capitalize",
                          fontSize: "14px",
                        }}
                      >
                        {item.status}
                      </span>
                    </Title>
                    <Text className="addon-description">
                      {item.phoneNumber}
                    </Text>
                    <br />
                    <Text className="addon-description">
                      {item.cost}$ Monthly
                    </Text>
                    <br />
                    <Text className="addon-description">
                      Next billing date:{" "}
                      {moment(billingDetails?.next_billing_at).format(
                        "DD MMMM YYYY"
                      )}
                    </Text>
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmationModalOpen(true);
                      setConfirmationModalData(item);
                    }}
                    type="text"
                    className="addon-button"
                    disabled={unsubscribeInProcess}
                  >
                    Unsubscribe
                  </Button>
                </List.Item>
              )}
            />
          )}
        </>
      ) : (
        <Text>No subscribed addons available.</Text>
      )}

      <Modal
        centered
        className="unsub-modal custom-modal"
        width={400}
        open={isConfirmationModalOpen}
        onCancel={() => setConfirmationModalOpen(false)}
        okText="Cancel Now"
        cancelText="Cancel Later"
        footer={false}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <div className="custom-cross-icon">
          <CloseOutlined />
        </div>

        <h2>Cancellation of Add-on</h2>

        <p>
          Do you want to cancel add-on “{confirmationModalData?.addonName}” now
          or at the end of billing date{" "}
          {moment(billingDetails?.next_billing_at).format("DD MMMM YYYY")}?
        </p>

        <div className="modal-footer">
          <Button
            disabled={unsubscribeInProcess}
            loading={cancelNowLoading}
            type="primary"
            className="modal-ok-btn"
            onClick={() => {
              setCancelNowLoading(true);
              handleDelete(confirmationModalData, "true");
            }}
          >
            Cancel Now
          </Button>
          <Button
            disabled={unsubscribeInProcess}
            loading={cancelLaterLoading}
            type="text"
            className="modal-cancel-btn"
            onClick={() => {
              setCancelLaterLoading(true);
              handleDelete(confirmationModalData, "false");
            }}
          >
            Cancel Later
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SubscribedAddonList;
